import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, CheckCircle, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import HelpLink from "@/components/HelpLink";
import AdminToggle from "@/components/admin/AdminToggle";

/**
 * Prescriber-side Pre-Screened queue.
 * - Lists orders flagged by `/orders/auto-approvable` (automated screening found
 *   no allergy/drug/medical-history flags). Backend path is unchanged for
 *   compatibility — only the UI verbiage has been updated to "Pre-Screened".
 * - Toggle for the tenant-wide pre-screening setting (hits `/admin/auto-approve`).
 * - "Quick Approve" button → PUT `/orders/{id}/action` with approve action.
 *   The clinician still makes the approval decision; the badge only indicates
 *   the order passed automated screening.
 * - "Custom Order" dialog → POST `/orders/custom` to create a manual order for a patient.
 *
 * `loadData` is the parent-level refresh so the main queue re-pulls after actions.
 */
export default function AutoApproveTab({ loadData, patients, onOpenOrderAction }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [customOrder, setCustomOrder] = useState(false);
  const [customForm, setCustomForm] = useState({ patient_id: "", patient_name: "", medication_name: "", notes: "" });
  // Server-backed patient typeahead. We query /prescriber/patients?q=... so
  // the dropdown isn't capped to the limited list passed via props.
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef(null);

  const loadAutoOrders = useCallback(async () => {
    try { const { data } = await API.get("/orders/auto-approvable"); setOrders(data); } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadAutoOrders();
    API.get("/admin/auto-approve").then(({ data }) => setAutoEnabled(data.enabled)).catch(() => {});
  }, [loadAutoOrders]);

  const toggleAutoApprove = async () => {
    try {
      const { data } = await API.put("/admin/auto-approve", { enabled: !autoEnabled });
      setAutoEnabled(data.enabled);
      toast.success(`Pre-screening ${data.enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error("Failed"); }
  };

  const handleQuickApprove = async (order) => {
    try {
      await API.put(`/orders/${order.id}/action`, { action: "approve", notes: "Approved from pre-screened queue (automated screening raised no flags — clinician confirmed)" });
      toast.success(`Order ${order.id.slice(0, 8)} approved!`);
      loadAutoOrders();
      loadData();
    } catch (e) { toast.error("Approval failed"); }
  };

  const handleCreateCustom = async () => {
    if (!customForm.patient_id || !customForm.medication_name) { toast.error("Patient and medication required"); return; }
    try {
      const { data: createdOrder } = await API.post("/orders/custom", customForm);
      toast.success("Custom order created — opening eRx writer…");
      setCustomOrder(false);
      setCustomForm({ patient_id: "", patient_name: "", medication_name: "", notes: "" });
      setPatientQuery("");
      setPatientResults([]);
      // Pull the freshest order list, then auto-open the action modal so
      // the prescriber can immediately approve / write the eRx — no extra
      // clicks. Parent dashboard owns the action modal so we delegate via
      // the onOpenOrderAction prop.
      await loadData();
      if (typeof onOpenOrderAction === "function" && createdOrder?.id) {
        onOpenOrderAction(createdOrder);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create");
    }
  };

  // Debounced server-side patient typeahead. /prescriber/patients?q=...
  // accepts a free-text query and returns matches on name + email + phone.
  useEffect(() => {
    if (!customOrder) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!patientQuery || patientQuery.trim().length < 2) {
      setPatientResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchingPatients(true);
      try {
        const { data } = await API.get(`/prescriber/patients?q=${encodeURIComponent(patientQuery)}&limit=25`);
        setPatientResults(Array.isArray(data) ? data : []);
      } catch {
        setPatientResults([]);
      } finally {
        setSearchingPatients(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [patientQuery, customOrder]);

  return (
    <div data-testid="auto-approve-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pre-Screened Queue
            <HelpLink sectionId="rx-auto-approve" title="Training: Pre-Screened (PS) Badge — What It Means" size={16} />
          </h1>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Orders that passed automated allergy, drug, and medical-history screening — clinician review still required before approval</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="rounded-lg border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-800" onClick={() => setCustomOrder(true)} data-testid="create-custom-order-btn">
            <FileText className="w-4 h-4 mr-1" /> Custom Order
          </Button>
          <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-700">
            <span className="text-xs text-slate-400 dark:text-slate-500">Pre-Screening</span>
            <AdminToggle
              checked={autoEnabled}
              onCheckedChange={toggleAutoApprove}
              testId="auto-approve-toggle"
              labels={{ on: "On", off: "Off" }}
              ariaLabel="Toggle pre-screening"
            />
          </div>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading...</div> : (
        <>
          <div className="space-y-3">
            {orders.map(order => (
              <div key={order.id} className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-5 flex items-center justify-between gap-4" data-testid={`auto-order-${order.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-slate-900 dark:text-white">{order.patient_name_full || order.patient_name}</p>
                    <span className="text-xs bg-emerald-900/50 text-emerald-400 px-2 py-0.5 rounded-full">Pre-Screened</span>
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-500">{order.medication_name} &middot; #{order.id?.slice(0, 8)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Allergies: {order.patient_allergies} | Meds: {(order.patient_medications || "None").slice(0, 50)}</p>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 dark:text-white rounded-lg" onClick={() => handleQuickApprove(order)} data-testid={`quick-approve-${order.id}`}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Quick Approve
                </Button>
              </div>
            ))}
            {orders.length === 0 && <div className="bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-300 dark:border-slate-700 p-8 text-center text-slate-400 dark:text-slate-500">No pre-screened orders in queue</div>}
          </div>
        </>
      )}

      <Dialog open={customOrder} onOpenChange={() => setCustomOrder(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Create Custom Order</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="custom-order-form">
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Patient</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  value={patientQuery}
                  onChange={(e) => { setPatientQuery(e.target.value); setShowResults(true); setCustomForm((f) => ({ ...f, patient_id: "", patient_name: "" })); }}
                  onFocus={() => setShowResults(true)}
                  placeholder="Type a name, email, or phone to search…"
                  className="pl-9 rounded-xl"
                  data-testid="custom-patient-search-input"
                  autoComplete="off"
                />
                {searchingPatients && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                {customForm.patient_id && !showResults && (
                  <div className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-300 font-medium" data-testid="custom-patient-selected">
                    ✓ Selected: {customForm.patient_name}
                  </div>
                )}
                {showResults && patientQuery.trim().length >= 2 && (
                  <div className="absolute z-20 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg" data-testid="custom-patient-results">
                    {patientResults.length === 0 && !searchingPatients && (
                      <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
                    )}
                    {patientResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setCustomForm((f) => ({ ...f, patient_id: p.id, patient_name: p.name || p.email }));
                          setPatientQuery(p.name || p.email);
                          setShowResults(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/30 text-sm border-b border-slate-100 dark:border-slate-800 last:border-0"
                        data-testid={`custom-patient-option-${p.id}`}
                      >
                        <div className="font-medium text-slate-900 dark:text-white">{p.name || "(no name)"}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{p.email}{p.phone ? ` · ${p.phone}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Medication Name</Label>
              <Input value={customForm.medication_name} onChange={(e) => setCustomForm({ ...customForm, medication_name: e.target.value })} placeholder="e.g. Semaglutide 0.5mg, Custom Compound" className="mt-1 rounded-xl" data-testid="custom-med-input" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-200">Notes</Label>
              <Textarea value={customForm.notes} onChange={(e) => setCustomForm({ ...customForm, notes: e.target.value })} placeholder="Special instructions, reason for custom order..." className="mt-1 rounded-xl min-h-[80px]" data-testid="custom-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOrder(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-slate-900 dark:text-white" onClick={handleCreateCustom} data-testid="create-custom-btn">Create Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
